import { isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, inject, isDevMode } from '@angular/core';

export type DevtoolsStateValueKind = 'signal' | 'subject' | 'observable' | 'value';

export interface DevtoolsStateValue {
  kind: DevtoolsStateValueKind;
  value: unknown;
}

export interface DevtoolsServiceSnapshot {
  name: string;
  updatedAt: string;
  state: Record<string, DevtoolsStateValue>;
}

type PreviewResult = { handled: true; value: unknown } | { handled: false };

@Injectable({
  providedIn: 'root'
})
export class DevtoolsStateRegistryService {
  readonly #platformId = inject(PLATFORM_ID);
  readonly #entries = new Map<string, { name: string; instance: object }>();
  readonly #storageKey = 'arcdx.devtools';
  readonly #queryParam = 'arcdxDevtools';
  readonly #maxPreviewDepth = 2;
  readonly #maxPreviewItems = 20;

  register(instance: object, name?: string): void {
    if (!isPlatformBrowser(this.#platformId)) return;
    if (!this.#isEnabled()) return;

    const resolvedName = name ?? instance.constructor?.name ?? 'AnonymousService';
    if (!this.#entries.has(resolvedName)) {
      this.#entries.set(resolvedName, { name: resolvedName, instance });
    }
  }

  snapshot(): DevtoolsServiceSnapshot[] {
    if (!isPlatformBrowser(this.#platformId)) return [];
    if (!this.#isEnabled()) return [];

    const now = new Date().toISOString();
    return Array.from(this.#entries.values())
      .map((entry) => ({
        name: entry.name,
        updatedAt: now,
        state: this.#extractState(entry.instance)
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  #extractState(instance: object): Record<string, DevtoolsStateValue> {
    const result: Record<string, DevtoolsStateValue> = {};
    const keys = Object.getOwnPropertyNames(instance);

    keys.forEach((key) => {
      if (key.startsWith('_')) return;
      const value = (instance as Record<string, unknown>)[key];
      const parsed = this.#toStateValue(value);
      if (parsed) {
        result[key] = parsed;
      }
    });

    if (isDevMode()) {
      result.__devtools = {
        kind: 'value',
        value: {
          keys: keys.length
        }
      };
    }

    return result;
  }

  #toStateValue(value: unknown): DevtoolsStateValue | null {
    if (this.#isSignal(value)) {
      return {
        kind: 'signal',
        value: this.#toPreviewValue(this.#safeCall(value as () => unknown))
      };
    }

    if (this.#isBehaviorSubject(value)) {
      return {
        kind: 'subject',
        value: this.#toPreviewValue(this.#safeSubjectValue(value as { value?: unknown; getValue?: () => unknown }))
      };
    }

    if (this.#isObservable(value)) {
      return {
        kind: 'observable',
        value: {
          type: value?.constructor?.name || 'Observable'
        }
      };
    }

    return { kind: 'value', value: this.#toPreviewValue(value) };
  }

  #toPreviewValue(value: unknown): unknown {
    return this.#preview(value, 0, new WeakSet<object>());
  }

  #preview(value: unknown, depth: number, seen: WeakSet<object>): unknown {
    if (value === null || value === undefined) return value;

    const primitiveOrSpecial = this.#previewPrimitiveOrSpecial(value);
    if (primitiveOrSpecial.handled) return primitiveOrSpecial.value;

    if (Array.isArray(value)) return this.#previewArray(value, depth, seen);
    if (value instanceof Map) return this.#previewMap(value, depth, seen);
    if (value instanceof Set) return this.#previewSet(value, depth, seen);
    if (typeof value === 'object') return this.#previewObject(value as Record<string, unknown>, depth, seen);

    return String(value);
  }

  #previewPrimitiveOrSpecial(value: unknown): PreviewResult {
    const valueType = typeof value;
    if (valueType === 'string' || valueType === 'number' || valueType === 'boolean') return { handled: true, value };
    if (valueType === 'bigint') return { handled: true, value: value.toString() };
    if (valueType === 'symbol') return { handled: true, value: String(value) };
    if (valueType === 'function') {
      const fn = value as Function;
      return { handled: true, value: `[Function ${fn.name || 'anonymous'}]` };
    }
    if (value instanceof Date) return { handled: true, value: value.toISOString() };
    if (value instanceof Error) {
      return {
        handled: true,
        value: {
          type: value.name || 'Error',
          message: value.message
        }
      };
    }
    return { handled: false };
  }

  #previewArray(value: unknown[], depth: number, seen: WeakSet<object>): unknown {
    if (depth >= this.#maxPreviewDepth) {
      return `[Array(${value.length})]`;
    }
    return {
      type: 'Array',
      length: value.length,
      items: value.slice(0, this.#maxPreviewItems).map((item) => this.#preview(item, depth + 1, seen)),
      truncated: value.length > this.#maxPreviewItems
    };
  }

  #previewMap(value: Map<unknown, unknown>, depth: number, seen: WeakSet<object>): unknown {
    if (depth >= this.#maxPreviewDepth) {
      return `[Map(${value.size})]`;
    }

    const entries: Array<{ key: unknown; value: unknown }> = [];
    for (const [entryKey, entryValue] of value.entries()) {
      if (entries.length >= this.#maxPreviewItems) break;
      entries.push({
        key: this.#preview(entryKey, depth + 1, seen),
        value: this.#preview(entryValue, depth + 1, seen)
      });
    }

    return {
      type: 'Map',
      size: value.size,
      entries,
      truncated: value.size > this.#maxPreviewItems
    };
  }

  #previewSet(value: Set<unknown>, depth: number, seen: WeakSet<object>): unknown {
    if (depth >= this.#maxPreviewDepth) {
      return `[Set(${value.size})]`;
    }

    const values = Array.from(value.values())
      .slice(0, this.#maxPreviewItems)
      .map((setValue) => this.#preview(setValue, depth + 1, seen));

    return {
      type: 'Set',
      size: value.size,
      values,
      truncated: value.size > this.#maxPreviewItems
    };
  }

  #previewObject(value: Record<string, unknown>, depth: number, seen: WeakSet<object>): unknown {
    if (seen.has(value)) return '[Circular]';
    seen.add(value);

    let keys: string[] = [];
    try {
      keys = Object.keys(value);
    } catch {
      return '[Unserializable object]';
    }

    if (depth >= this.#maxPreviewDepth) {
      return `[Object(${keys.length})]`;
    }

    const sampleKeys = keys.slice(0, this.#maxPreviewItems);
    const values: Record<string, unknown> = {};
    sampleKeys.forEach((key) => {
      values[key] = this.#preview(value[key], depth + 1, seen);
    });

    const constructorName = value.constructor?.name || 'Object';
    return {
      type: constructorName,
      keyCount: keys.length,
      values,
      truncated: keys.length > this.#maxPreviewItems
    };
  }

  #isSignal(value: unknown): boolean {
    if (typeof value !== 'function') return false;
    if (value.length > 0) return false;
    if ('subscribe' in value) return false;
    return true;
  }

  #isBehaviorSubject(value: unknown): boolean {
    if (!this.#isObservable(value)) return false;
    return 'getValue' in value || 'value' in value;
  }

  #isObservable(value: unknown): value is Record<string, unknown> {
    if (!value || (typeof value !== 'object' && typeof value !== 'function')) return false;
    const subscribe = Reflect.get(value, 'subscribe');
    return typeof subscribe === 'function';
  }

  #safeCall(fn: () => unknown): unknown {
    try {
      return fn();
    } catch {
      return null;
    }
  }

  #safeSubjectValue(subject: { value?: unknown; getValue?: () => unknown }): unknown {
    if (typeof subject.getValue === 'function') {
      try {
        return subject.getValue();
      } catch {
        return null;
      }
    }
    return subject.value ?? null;
  }

  #isEnabled(): boolean {
    if (isDevMode()) return true;

    try {
      const queryEnabled = new URLSearchParams(window.location.search).get(this.#queryParam) === '1';
      const storageEnabled = window.localStorage.getItem(this.#storageKey) === 'true';
      return queryEnabled || storageEnabled;
    } catch {
      return false;
    }
  }
}

export const registerDevtoolsState = (instance: object, name?: string): void => {
  const registry = inject(DevtoolsStateRegistryService, { optional: true });
  registry?.register(instance, name);
};
